<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import KpEchCodeSelect from '@/components/KpEchCodeSelect.vue';
import Card from 'primevue/card';
import { TYPES } from '@/models/EchCode';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import KpSelectField from '@/components/KpSelectField.vue';
import { KennzahlNutzungsart } from '@/models/KennzahlNutzungsart';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import { Nutzungsart, NutzungsartCollection } from '@/models/Nutzungsart';
import KpNumberField from '@/components/KpNumberField.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<KennzahlNutzungsart | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    nutzungsart: createValidator(
        computed(() => record.value?.nutzungsart_id),
        [validateNotEmpty],
    ),
    bauperiode: createValidator(
        computed(() => record.value?.bauperiode_id),
        [validateNotEmpty],
    ),
    umrechnungsfaktor_geschossflaeche_ebf: createValidator(
        computed(() => record.value?.umrechnungsfaktor_geschossflaeche_ebf),
        [validateNotEmpty],
    ),
    umrechnungsfaktor_wohnungsflaeche_ebf: createValidator(
        computed(() => record.value?.umrechnungsfaktor_wohnungsflaeche_ebf),
        [validateNotEmpty],
    ),
    stuetzpunkt_unten: createValidator(
        computed(() => record.value?.stuetzpunkt_unten),
        [validateNotEmpty],
    ),
    standadverbauch_stuetzpunkt_unten: createValidator(
        computed(() => record.value?.standadverbauch_stuetzpunkt_unten),
        [validateNotEmpty],
    ),
    stuetzpunkt_oben: createValidator(
        computed(() => record.value?.stuetzpunkt_oben),
        [validateNotEmpty],
    ),
    standadverbauch_stuetzpunkt_oben: createValidator(
        computed(() => record.value?.standadverbauch_stuetzpunkt_oben),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(KennzahlNutzungsart);
    } else {
        try {
            const rec = await KennzahlNutzungsart.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_kennzahl_nutzungsart_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_kennzahl_nutzungsart' });
    }
}

const nutzungsarten: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => nutzungsarten.value.push(m));
});
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('kennzahlen_nutzungsart') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpSelectField
                        v-model="record.nutzungsart_id"
                        class="col-6"
                        fluid
                        :label="$t('nutzungsart')"
                        :options="nutzungsarten"
                        option-label="bezeichnung"
                        option-value="id"
                        :invalid="!validators.nutzungsart.isValid.value"
                        :error="validators.nutzungsart.msg.value"
                    ></KpSelectField>
                    <KpEchCodeSelect
                        v-model="record.bauperiode_id"
                        :type="TYPES.bauperiode"
                        class="col-6"
                        fluid
                        :label="$t('bauperiode')"
                        :invalid="!validators.bauperiode.isValid.value"
                        :error="validators.bauperiode.msg.value"
                    ></KpEchCodeSelect>
                </div>

                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.umrechnungsfaktor_geschossflaeche_ebf"
                        :label="$t('umrechnungsfaktor_geschossflaeche_ebf')"
                        class="col-6"
                        :maxFractionDigits="5"
                        fluid
                        :invalid="!validators.umrechnungsfaktor_geschossflaeche_ebf.isValid.value"
                        :error="validators.umrechnungsfaktor_geschossflaeche_ebf.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.umrechnungsfaktor_wohnungsflaeche_ebf"
                        :label="$t('umrechnungsfaktor_wohnungsflaeche_ebf')"
                        class="col-6"
                        :maxFractionDigits="5"
                        fluid
                        :invalid="!validators.umrechnungsfaktor_wohnungsflaeche_ebf.isValid.value"
                        :error="validators.umrechnungsfaktor_wohnungsflaeche_ebf.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.stuetzpunkt_unten"
                        :label="$t('stuetzpunkt_unten')"
                        class="col-6"
                        fluid
                        :invalid="!validators.stuetzpunkt_unten.isValid.value"
                        :error="validators.stuetzpunkt_unten.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.standadverbauch_stuetzpunkt_unten"
                        :label="$t('standadverbauch_stuetzpunkt_unten')"
                        class="col-6"
                        fluid
                        :invalid="!validators.standadverbauch_stuetzpunkt_unten.isValid.value"
                        :error="validators.standadverbauch_stuetzpunkt_unten.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.stuetzpunkt_oben"
                        :label="$t('stuetzpunkt_oben')"
                        class="col-6"
                        fluid
                        :invalid="!validators.stuetzpunkt_oben.isValid.value"
                        :error="validators.stuetzpunkt_oben.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.standadverbauch_stuetzpunkt_oben"
                        :label="$t('standadverbauch_stuetzpunkt_oben')"
                        class="col-6"
                        fluid
                        :invalid="!validators.standadverbauch_stuetzpunkt_oben.isValid.value"
                        :error="validators.standadverbauch_stuetzpunkt_oben.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.abweichung_standardverbrauch"
                        :label="$t('abweichung_standardverbrauch')"
                        class="col-6"
                        fluid
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.begrenzung_energiebezugsflaeche"
                        :label="$t('begrenzung_energiebezugsflaeche')"
                        class="col-6"
                        fluid
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.warmwasserbedarf_pro_wohneinheit"
                        :label="$t('warmwasserbedarf_pro_wohneinheit')"
                        class="col-6"
                        fluid
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.warmwasserbedarf_pro_m2_ebf"
                        :label="$t('warmwasserbedarf_pro_m2_ebf')"
                        class="col-6"
                        fluid
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
